import cn from 'classnames';
import { motion, useMotionValue, useAnimationFrame, useReducedMotion } from 'motion/react';
import { useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';

const DEFAULT_BUFFER_MULTIPLIER = 2;
const DEFAULT_MIN_CLONE_SETS = 2;
const DEFAULT_MAX_CLONE_SETS = 10;
const DEFAULT_SPEED = 75;

const Ticker = ({
  images,
  containerClassName,
  imageWrapperClassName,
  imageClassName,
  bufferMultiplier = DEFAULT_BUFFER_MULTIPLIER,
  maxCloneSets = DEFAULT_MAX_CLONE_SETS,
  speed = DEFAULT_SPEED,
}) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [imageSetWidth, setImageSetWidth] = useState(0);
  const [isInitialMeasurement, setIsInitialMeasurement] = useState(true);

  const prefersReducedMotion = useReducedMotion();
  const x = useMotionValue(0);

  const cloneCount = useMemo(() => {
    if (!containerWidth || !imageSetWidth) {
      return DEFAULT_MIN_CLONE_SETS;
    }

    const targetWidth = containerWidth * bufferMultiplier;
    const requiredClones = Math.ceil(targetWidth / imageSetWidth);

    return Math.max(DEFAULT_MIN_CLONE_SETS, Math.min(requiredClones, maxCloneSets));
  }, [containerWidth, imageSetWidth, bufferMultiplier, maxCloneSets]);

  const clonedImages = useMemo(() => {
    if (!images || !Array.isArray(images)) return [];

    return Array.from({ length: cloneCount }, (_, setIndex) =>
      images.map((image, imageIndex) => ({
        ...image,
        key: `${setIndex}-${imageIndex}`,
        setIndex,
        imageIndex,
      }))
    ).flat();
  }, [images, cloneCount]);

  useAnimationFrame((_, delta) => {
    if (!imageSetWidth || imageSetWidth === 0) return;

    if (prefersReducedMotion) return;

    const pixelsToMove = (speed * delta) / 1000;
    const currentX = x.get();
    const newX = currentX - pixelsToMove;

    if (Math.abs(newX) >= imageSetWidth) {
      x.set(newX + imageSetWidth);
    } else {
      x.set(newX);
    }
  });

  const waitForMedia = useCallback(element => {
    if (element.tagName === 'VIDEO') {
      if (element.readyState >= 1) {
        return Promise.resolve();
      }

      return new Promise(resolve => {
        element.onloadedmetadata = resolve;
        element.onerror = resolve;
      });
    }

    if (typeof element.decode === 'function') {
      return element.decode().catch(() => {});
    }

    if (element.complete) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      element.onload = resolve;
      element.onerror = resolve;
    });
  }, []);

  const measureImageSetWidth = useCallback((container, cloneCount) => {
    if (!container || !cloneCount) {
      return 0;
    }

    const firstChild = container.firstElementChild;
    const lastChild = container.lastElementChild;

    if (!firstChild || !lastChild) {
      return 0;
    }

    const leftEdge = firstChild.getBoundingClientRect().left;
    const rightEdge = lastChild.getBoundingClientRect().right;
    const totalWidth = rightEdge - leftEdge;

    return totalWidth / cloneCount;
  }, []);

  const measureDimensions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    setContainerWidth(containerRect.width);

    if (isInitialMeasurement && container.children.length > 0) {
      const mediaElements = Array.from(container.querySelectorAll('img, video'));
      const unloadedMedia = mediaElements.filter(element => {
        if (element.tagName === 'VIDEO') {
          return element.readyState < 1;
        }
        return !element.complete;
      });

      if (unloadedMedia.length > 0) {
        Promise.all(unloadedMedia.map(waitForMedia)).then(() => {
          if (containerRef.current) {
            measureDimensions();
          }
        });
        return;
      }

      const singleSetWidth = measureImageSetWidth(container, cloneCount);
      setImageSetWidth(singleSetWidth);
      setIsInitialMeasurement(false);
    }
  }, [isInitialMeasurement, waitForMedia, measureImageSetWidth, cloneCount]);

  useLayoutEffect(() => {
    measureDimensions();

    const resizeObserver = new ResizeObserver(measureDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [images?.length, isInitialMeasurement, measureDimensions]);

  return (
    <div className={cn('overflow-hidden', containerClassName)}>
      <motion.div
        ref={containerRef}
        style={{
          x,
          display: 'flex',
          flexDirection: 'row',
          willChange: 'transform',
        }}
      >
        {clonedImages.map(item => {
          if (item.type === 'video') {
            return (
              <div key={item.key} className={imageWrapperClassName}>
                <video
                  src={item.src}
                  className={`!h-full w-full object-cover ${imageClassName}`}
                  autoPlay={!prefersReducedMotion}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={item.alt}
                />
              </div>
            );
          }
          return (
            <div key={item.key} className={imageWrapperClassName}>
              <img
                src={item.src}
                alt={item.alt}
                className={`!h-full w-auto object-cover ${imageClassName}`}
              />
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

Ticker.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
      type: PropTypes.oneOf(['image', 'video']),
    })
  ).isRequired,
  containerClassName: PropTypes.string,
  imageWrapperClassName: PropTypes.string,
  imageClassName: PropTypes.string,
  bufferMultiplier: PropTypes.number,
  maxCloneSets: PropTypes.number,
  speed: PropTypes.number,
};

export default Ticker;
